import { useEffect, useState, useRef } from 'react';
import { Box, Typography, Paper, CircularProgress, Alert, Button, MenuItem, Select, IconButton } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import WorkflowStepsDrawer from './WorkflowStepsDrawer';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonatePlainIconButtonSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateUploadOutlinedSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogFormContentSx,
  harmonateFormFieldsSx,
  harmonateTextFieldSx,
  harmonateInputLabelProps,
  harmonateSelectMenuProps,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const WorkflowDefinition = () => {
  const { canAdd, canEdit } = useFunctionPermissions(FC.WORKFLOW_DEFINITION);
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedRows, setEditedRows] = useState({});
  const [originalRows, setOriginalRows] = useState({});
  const [form, setForm] = useState({
    name: '',
    description: '',
    documentTypeId: '',
    snowflakeTableName: '',
    runCondition: '',
    validationView: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [stepsDrawerOpen, setStepsDrawerOpen] = useState(false);
  const [stepsWorkflowId, setStepsWorkflowId] = useState(null);
  const [documentTypeOptions, setDocumentTypeOptions] = useState([]);
  const [documentTypeOptionsLoading, setDocumentTypeOptionsLoading] = useState(false);
  const clickTimer = useRef(null);

  const getTenantId = async () => {
    return localStorage.getItem('tenant_id');
  };

  const fetchDocumentTypeOptions = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const tenantId = await getTenantId();
      if (!tenantId) throw new Error('No tenant ID found');

      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/document-types`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!resp.ok) {
        throw new Error(
          `Failed to fetch document types options: ${resp.status} ${resp.statusText}`
        );
      }

      const data = await resp.json();
      return Array.isArray(data)
        ? data.filter((item) => item.name !== "Unidentified")
        : [];
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  useEffect(() => {
    const fetchOptions = async () => {
      setDocumentTypeOptionsLoading(true);
      const options = await fetchDocumentTypeOptions();
      setDocumentTypeOptions(options);
      setDocumentTypeOptionsLoading(false);
    };
    fetchOptions();
  }, []);

  const fetchWorkflows = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/workflow-definition-snowflake`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!resp.ok) throw new Error(`Failed to fetch workflows: ${resp.status} ${resp.statusText}`);
      const data = await resp.json();
      const workflowsWithIds = Array.isArray(data) ? data : [];
      setWorkflows(workflowsWithIds);

      // Initialize original rows for editing
      const originalData = {};
      workflowsWithIds.forEach(workflow => {
        originalData[workflow.id] = { ...workflow };
      });
      setOriginalRows(originalData);
    } catch (err) {
      setError(err.message);
      setWorkflows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    if (!canAdd) return;
    setForm({
      name: '',
      description: '',
      documentTypeId: '',
      snowflakeTableName: '',
      runCondition: '',
      validationView: ''
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setForm({
      name: '',
      description: '',
      documentTypeId: '',
      snowflakeTableName: '',
      runCondition: '',
      validationView: ''
    });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleDocumentTypeChange = (e) => {
    setForm({ ...form, documentTypeId: e.target.value });
  };

  const handleSubmit = async (e) => {
    if (!canAdd) return;
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const url = `${process.env.REACT_APP_BACKEND_URL}conductor/workflow-definition-snowflake`;
      const method = 'POST';
      const resp = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: form.name,
          description: form.description,
          documentTypeId: form.documentTypeId,
          snowflakeTableName: form.snowflakeTableName,
          runCondition: form.runCondition || null,
          validationView: form.validationView || null
        }),
      });
      if (!resp.ok) throw new Error('Failed to create workflow definition');
      handleCloseDialog();
      fetchWorkflows();
    } catch (err) {
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditModeToggle = () => {
    if (!canEdit) return;
    if (isEditMode) {
      // Exit edit mode - discard changes
      setEditedRows({});
      setIsEditMode(false);
    } else {
      // Enter edit mode
      setIsEditMode(true);
    }
  };

  const processRowUpdate = async (newRow, oldRow) => {
    const changedField = Object.keys(newRow).find(field => newRow[field] !== oldRow[field]);
    if (!changedField) return newRow;

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      // Get the complete original workflow data
      let originalWorkflow = originalRows[newRow.id];
      if (!originalWorkflow) {
        const fallbackWorkflow = workflows.find(w => w.id === newRow.id);
        if (fallbackWorkflow) {
          originalWorkflow = fallbackWorkflow;
        } else {
          throw new Error('Original workflow data not found');
        }
      }

      const payload = {
        name: newRow.name,
        description: newRow.description,
        documentTypeId: newRow.documentTypeId,
        snowflakeTableName: newRow.snowflakeTableName,
        runCondition: newRow.runCondition || null,
        validationView: newRow.validationView || null
      };

      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/workflow-definition-snowflake/${newRow.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!resp.ok) throw new Error(`Failed to update workflow definition: ${resp.status}`);

      // Update original rows
      setOriginalRows(prev => ({
        ...prev,
        [newRow.id]: { ...originalWorkflow, ...newRow }
      }));

      fetchWorkflows();
      return { ...newRow, ...payload };
    } catch (err) {
      alert(`Failed to update: ${err.message}`);
      return oldRow;
    }
  };

  const handleProcessRowUpdateError = (error) => {
    alert(`Update error: ${error.message}`);
  };

  useEffect(() => {
    fetchWorkflows();
  }, []);

  if (loading) {
    return (
      <Box sx={{ ...harmonatePageSx, height: '100vh', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress sx={{ color: ht.white }} />
        <Typography variant="body2" sx={{ ml: 2, color: ht.white }}>
          Loading workflow definitions...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ ...harmonatePageSx, height: '100vh', justifyContent: 'center', alignItems: 'center' }}>
        <Alert severity="error" sx={{ mb: 3, maxWidth: '500px' }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Failed to load workflow definitions
          </Typography>
          <Typography variant="body2">
            {error}
          </Typography>
        </Alert>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={fetchWorkflows}
          sx={harmonateProceedButtonSx}
        >
          Retry
        </Button>
      </Box>
    );
  }

  const columns = [
    {
      field: 'name',
      headerName: 'Workflow Name',
      flex: 1,
      minWidth: 200,
      editable: isEditMode,
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 170,
      editable: isEditMode,
    },
    {
      field: 'documentTypeId',
      headerName: 'Document Type',
      flex: 1,
      minWidth: 170,
      editable: isEditMode,
      renderCell: (params) => {
        const id = params.value;
        const found = documentTypeOptions.find(opt => String(opt.id) === String(id));
        const name = found ? found.name : id || '';
        return (
          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            alignItems: 'center',
            height: '100%'
          }}>
            <Box
              sx={{
                bgcolor: ht.secondaryTab,
                color: ht.text,
                px: 1.5,
                py: 0.25,
                borderRadius: ht.buttonRadius,
                fontSize: '0.75rem',
                fontWeight: 500,
              }}
            >
              {name}
            </Box>
          </Box>
        );
      },
      renderEditCell: (params) => (
        <Select
          value={params.value || ''}
          onChange={e => {
            params.api.setEditCellValue({ id: params.id, field: params.field, value: e.target.value }, e);
          }}
          fullWidth
          size="small"
          renderValue={(selected) => {
            const found = documentTypeOptions.find(opt => String(opt.id) === String(selected));
            return found ? found.name : selected || '';
          }}
          MenuProps={harmonateSelectMenuProps}
          sx={{
            borderRadius: ht.fieldRadius,
            fontSize: '0.875rem',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: ht.text },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: ht.text },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ht.darkBlue },
          }}
        >
          <MenuItem value="" sx={{ fontSize: '0.875rem' }}>
            <em>None</em>
          </MenuItem>
          {documentTypeOptions.map(opt => (
            <MenuItem key={opt.id} value={opt.id} sx={{ fontSize: '0.875rem' }}>{opt.name}</MenuItem>
          ))}
        </Select>
      )
    },
    {
      field: 'snowflakeTableName',
      headerName: 'Snowflake Table',
      flex: 1,
      minWidth: 170,
      editable: isEditMode,
    },
    {
      field: 'runCondition',
      headerName: 'Run Condition',
      flex: 1,
      minWidth: 250,
      editable: isEditMode,
      renderCell: (params) => (
        <Box sx={{
          whiteSpace: 'normal',
          lineHeight: '1.2',
          py: 1,
          width: '100%',
          fontSize: '0.75rem',
          color: params.value ? ht.text : ht.fadedText,
          fontStyle: params.value ? 'normal' : 'italic'
        }}>
          {params.value || 'No run condition set'}
        </Box>
      )
    },
    {
      field: 'validationView',
      headerName: 'Validation View',
      flex: 1,
      minWidth: 180,
      editable: isEditMode,
    },
  ];

  return (
    <Box sx={{ ...harmonatePageSx, height: '100vh' }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
        flexShrink: 0,
      }}>
        <Typography variant="h6" sx={harmonatePageTitleSx}>
          Workflow Definitions
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            onClick={handleEditModeToggle}
            disabled={!canEdit}
            sx={{
              ...harmonatePlainIconButtonSx,
              color: isEditMode ? ht.secondaryTab : ht.white,
            }}
            title={!canEdit ? 'No edit permission' : isEditMode ? 'Exit Edit Mode' : 'Enter Edit Mode'}
          >
            <EditOutlinedIcon />
          </IconButton>
          <IconButton
            onClick={handleOpenDialog}
            disabled={!canAdd}
            sx={harmonatePlainIconButtonSx}
            title={canAdd ? 'Add New Workflow' : 'No add permission'}
          >
            <AddIcon />
          </IconButton>
        </Box>
      </Box>

      <Paper elevation={0} sx={{
        ...harmonateCardSx,
        flex: 1,
        minHeight: 0,
        mb: 3,
        display: 'flex',
        flexDirection: 'column'
      }}>
        <DataGridPro
          columnResizeMode="onEnd"
          resizable
          pagination
          rowsPerPageOptions={[10, 25, 50]}
          pageSize={10}
          autoPageSize={false}
          autoHeight={false}
          rows={workflows.map((workflow, idx) => ({
            id: workflow.id || idx + 1,
            name: workflow.name || '',
            description: workflow.description || '',
            documentTypeId: workflow.documentTypeId || '',
            snowflakeTableName: workflow.snowflakeTableName || '',
            runCondition: workflow.runCondition || '',
            validationView: workflow.validationView || ''
          }))}
          columns={columns}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={handleProcessRowUpdateError}
          editMode="cell"
          sx={{
            ...harmonateDataGridSx,
            flex: 1,
            minHeight: 0,
            height: '100%',
            '& .MuiDataGrid-row:hover': {
              bgcolor: isEditMode ? 'transparent' : ht.secondaryTab,
              cursor: isEditMode ? 'default' : 'pointer'
            },
            '& .MuiDataGrid-cell:focus': { outline: 'none' },
            '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
            '& .MuiDataGrid-cell--editable': {
              cursor: 'text',
              '&:hover': {
                backgroundColor: ht.secondaryTab,
              }
            }
          }}
          disableSelectionOnClick
          getRowId={(row) => row.id}
          rowHeight={40}
          onRowClick={(params) => {
            if (!isEditMode) {
              if (clickTimer.current) {
                clearTimeout(clickTimer.current);
                clickTimer.current = null;
              }
              setStepsWorkflowId(params.row.id);
              setStepsDrawerOpen(true);
            }
          }}
          onCellClick={(params, event) => {
            if (isEditMode && params.field && columns.find(col => col.field === params.field)?.editable) {
              event.stopPropagation();
            }
          }}
          components={{
            NoRowsOverlay: () => (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%'
              }}>
                <Typography variant="body1" sx={{ mb: 2, color: ht.text }}>
                  No workflow definitions available
                </Typography>
                <Button
                  variant="outlined"
                  onClick={fetchWorkflows}
                  sx={harmonateUploadOutlinedSx}
                >
                  Refresh
                </Button>
              </Box>
            )
          }}
        />
      </Paper>

      <WorkflowStepsDrawer
        open={stepsDrawerOpen}
        onClose={() => setStepsDrawerOpen(false)}
        workflowId={stepsWorkflowId}
      />

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>
          Add Workflow Definition
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
            <TextField
              label="Workflow Name"
              name="name"
              value={form.name}
              onChange={handleFormChange}
              required
              fullWidth
              size="small"
              InputLabelProps={harmonateInputLabelProps}
              sx={harmonateTextFieldSx}
            />
            <TextField
              label="Description"
              name="description"
              value={form.description}
              onChange={handleFormChange}
              fullWidth
              multiline
              rows={3}
              size="small"
              InputLabelProps={harmonateInputLabelProps}
              sx={harmonateTextFieldSx}
            />
            <TextField
              select
              label="Document Type"
              name="documentTypeId"
              value={form.documentTypeId || ''}
              onChange={handleDocumentTypeChange}
              fullWidth
              size="small"
              disabled={documentTypeOptionsLoading}
              InputLabelProps={harmonateInputLabelProps}
              SelectProps={{ MenuProps: harmonateSelectMenuProps }}
              sx={harmonateTextFieldSx}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {documentTypeOptions.map(opt => (
                <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label="Snowflake Table Name"
              name="snowflakeTableName"
              value={form.snowflakeTableName}
              onChange={handleFormChange}
              required
              size="small"
              InputLabelProps={harmonateInputLabelProps}
              sx={harmonateTextFieldSx}
            />
            <TextField
              fullWidth
              label="Run Condition (Optional)"
              name="runCondition"
              value={form.runCondition}
              onChange={handleFormChange}
              size="small"
              placeholder="e.g., WORKFLOW.MY_CONDITION"
              InputLabelProps={harmonateInputLabelProps}
              sx={harmonateTextFieldSx}
            />
            <TextField
              fullWidth
              label="Validation View (Optional)"
              name="validationView"
              value={form.validationView}
              onChange={handleFormChange}
              size="small"
              placeholder="e.g., vw_validation_001"
              InputLabelProps={harmonateInputLabelProps}
              sx={harmonateTextFieldSx}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleCloseDialog} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              sx={harmonateProceedButtonSx}
            >
              {submitting ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default WorkflowDefinition;